import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { SwapTeamIllustration } from '@/components/swap/SwapTeamIllustration';
import { SWAP_MATCHDAY, SWAP_WINDOW_CLOSES_AT } from '@/constants';
import { MODAL_FOOTER_PB, MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { formatFixtureKickoffLondon } from '@/helpers';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';

export function SwapWindowHelpBody() {
  const swapWindowCloseLondon = formatFixtureKickoffLondon(SWAP_WINDOW_CLOSES_AT).replace(', ', ' on ');

  return (
    <VStack spacing={4} align='stretch'>
      <Text color='chakra-body-text'>
        The Round of 16 is complete. You may now swap <strong>two teams</strong> from your list to try to maximise
        your points.
      </Text>
      <SwapTeamIllustration />
      <Text color='chakra-body-text'>
        You can change your mind as many times as you like before the window closes — whatever you save when it
        closes is final.
      </Text>
      <Text color='chakra-body-text'>
        Your original ranking still applies to Matchdays 1–{SWAP_MATCHDAY}. From Matchday {SWAP_MATCHDAY + 1}{' '}
        (quarter-finals) onward, the swapped multipliers apply. Points already settled are not recalculated.
      </Text>
      <Text color='chakra-body-text'>
        Use the <strong>Swap Teams</strong> button on your profile while the window is open. The window closes at{' '}
        <strong>{swapWindowCloseLondon} 2026</strong> (UK time), when the quarter-finals begin.
      </Text>
    </VStack>
  );
}

/** Standalone swap-window announcement (AuthLayout page-load prompt). */
export function SwapWindowHelpModal(props: { isOpen: boolean; onClose: () => void }) {
  const { isOpen, onClose } = props;
  const { modalSize, isCentered, scrollBehavior, contentProps, bodyProps, footerPb } = useMobileFormModal();

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={isCentered} size={modalSize} scrollBehavior={scrollBehavior}>
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <ModalHeader pt={MODAL_HEADER_PT} pb={2} flexShrink={0}>
          <Heading fontSize={{ base: 'xl', md: '2xl' }} color='chakra-heading-text'>
            Swap Window Open
          </Heading>
        </ModalHeader>
        <ModalCloseButton mt={2} mr={2} />
        <ModalBody {...bodyProps} px={{ base: 3, md: 6 }}>
          <SwapWindowHelpBody />
        </ModalBody>
        <ModalFooter pb={footerPb ?? MODAL_FOOTER_PB} flexShrink={0}>
          <Button w={{ base: 'full', md: 'auto' }} onClick={onClose} colorScheme='green'>
            Ok
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** In-modal help panel — same content as `SwapWindowHelpModal` without a second overlay. */
export function SwapWindowHelpPanel(props: { onClose: () => void }) {
  const { bodyProps, footerPb } = useMobileFormModal();

  return (
    <>
      <ModalHeader pt={MODAL_HEADER_PT} pb={2} flexShrink={0}>
        <Heading fontSize={{ base: 'xl', md: '2xl' }} color='chakra-heading-text'>
          Swap Window Open
        </Heading>
      </ModalHeader>
      <ModalCloseButton mt={2} mr={2} />
      <ModalBody {...bodyProps} px={{ base: 3, md: 6 }}>
        <SwapWindowHelpBody />
      </ModalBody>
      <ModalFooter pb={footerPb ?? MODAL_FOOTER_PB} flexShrink={0}>
        <Button w={{ base: 'full', md: 'auto' }} onClick={props.onClose} colorScheme='green'>
          Ok
        </Button>
      </ModalFooter>
    </>
  );
}
